// Estados visuais do cardápio: loading (skeleton), indisponível (gate), erro de
// rede (com retry) e vazio (sem itens). Todos honestos e amigáveis — nunca
// stack trace, nunca tela branca.

// Skeleton: imita 2 categorias com cards enquanto carrega. `aria-busy` + texto
// para leitor de tela.
export function LoadingState() {
  return (
    <div className="shell" role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only" style={srOnly}>
        Carregando o cardápio…
      </span>
      {[0, 1].map((c) => (
        <div className="skeleton-cat" key={c} aria-hidden="true">
          <div className="sk title" />
          {[0, 1, 2].map((i) => (
            <div className="sk card" key={i} />
          ))}
        </div>
      ))}
    </div>
  );
}

// Gate de entitlement / slug inexistente: mensagem calorosa, sem erro cru.
// QA-28 M-09 — ganha "Tentar de novo" (opcional): 401/403/404 também acontece por sessão/gate transitório e
// o cliente ficava sem NENHUMA ação na tela, só com a instrução de "conferir o link".
export function UnavailableState({ onRetry }: { onRetry?: () => void } = {}) {
  return (
    <div className="state" role="status">
      <div className="emoji" aria-hidden="true">
        🍽️
      </div>
      <h2>Cardápio não disponível</h2>
      <p>
        Não encontramos um cardápio digital para este endereço. Confira o link com o estabelecimento
        ou tente novamente mais tarde.
      </p>
      {onRetry && (
        <button type="button" className="btn" onClick={onRetry}>
          Tentar de novo
        </button>
      )}
    </div>
  );
}

// Erro de rede: honesto, com ação de tentar de novo.
export function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="state" role="alert">
      <div className="emoji" aria-hidden="true">
        📶
      </div>
      <h2>Não foi possível carregar</h2>
      <p>Parece que a conexão falhou. Verifique sua internet e tente novamente.</p>
      <button type="button" className="btn" onClick={onRetry}>
        Tentar de novo
      </button>
    </div>
  );
}

// Cardápio sem nenhum item (backend respondeu, mas sem produtos ativos).
// QA-28 M-09 — vazio com CTA: quando a loja tem WhatsApp, oferece o contato (o cliente pode pedir/perguntar
// mesmo com o cardápio ainda em cadastro) em vez de só "volte em instantes".
export function EmptyState({ whatsappHref }: { whatsappHref?: string | null } = {}) {
  return (
    <div className="state" role="status">
      <div className="emoji" aria-hidden="true">
        👩‍🍳
      </div>
      <h2>Cardápio em preparação</h2>
      <p>Os itens ainda estão sendo cadastrados. Volte em instantes - vai valer a pena.</p>
      {whatsappHref && (
        <a className="btn" href={whatsappHref} target="_blank" rel="noreferrer">
          <span aria-hidden="true">💬</span> Falar no WhatsApp
        </a>
      )}
    </div>
  );
}

// Sem resultados para a busca (dentro de um cardápio com itens).
// QA-28 M-09 — CTA "Limpar busca": sem ele a única saída era o "×" de 30px dentro do campo.
export function NoResultsState({ termo, onClear }: { termo: string; onClear?: () => void }) {
  return (
    <div className="state" role="status">
      <div className="emoji" aria-hidden="true">
        🔎
      </div>
      <h2>Nada encontrado</h2>
      <p>
        Não achamos itens para <strong>“{termo}”</strong>. Tente outro nome.
      </p>
      {onClear && (
        <button type="button" className="btn" onClick={onClear}>
          Limpar busca
        </button>
      )}
    </div>
  );
}

/** QA-28 B-05 — estilo utilitário de texto só-para-leitor-de-tela, exportado para o carrossel do QuickView
 *  (posição "Foto X de Y") reusar o MESMO recorte em vez de duplicar/inventar outro. */
export const srOnly: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};
